/**
 * useMediaQuery Hook
 * Story 12.16 - Mobile Filter Collapse Pattern
 *
 * Reactive hook for CSS media query matching.
 * Consolidates duplicated matchMedia logic from:
 * - PageTransition.tsx
 * - FavoriteButton.tsx
 * - MagicalLoader.tsx
 *
 * @module hooks/useMediaQuery
 */

import { useState, useEffect } from 'react';

/**
 * Hook to reactively match a CSS media query
 *
 * @param query - CSS media query string (e.g., '(max-width: 639px)')
 * @returns Boolean indicating if the media query matches
 *
 * @example
 * ```tsx
 * const isMobile = useMediaQuery('(max-width: 639px)');
 * const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // SSR-safe: check if window exists
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent): void => {
      setMatches(e.matches);
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handler);

    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

/**
 * Preset media query hooks for common breakpoints
 */

/**
 * Returns true when viewport is below Tailwind's `sm` breakpoint (640px)
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 639px)');
}

/**
 * Returns true when user prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}
